import { useEffect, useRef } from 'react';
import i18n from '@/i18n';

const supportedLngs = ['en', 'de'] as const;

/** Map backend defaultLanguage (e.g. "en", "de", "eng", "deu") to i18n language code. */
export function toI18nLanguage(code: string | undefined): (typeof supportedLngs)[number] {
  if (!code) return 'en';
  const lower = code.toLowerCase();
  if (lower === 'en' || lower === 'eng') return 'en';
  if (lower === 'de' || lower === 'deu') return 'de';
  return supportedLngs.includes(lower as (typeof supportedLngs)[number])
    ? (lower as (typeof supportedLngs)[number])
    : 'en';
}

/**
 * Switches i18n to the given restaurant's defaultLanguage for the lifetime of
 * the calling component, then restores the previous language on unmount.
 *
 * Used on both the public storefront and the admin restaurant dashboard.
 */
export function useRestaurantLanguage(defaultLanguage: string | undefined) {
  const previousLanguageRef = useRef<string | null>(null);

  useEffect(() => {
    if (!defaultLanguage) return;

    const targetLng = toI18nLanguage(defaultLanguage);
    const currentLng = i18n.language?.split('-')[0] ?? i18n.language;

    if (currentLng !== targetLng) {
      previousLanguageRef.current = i18n.language ?? null;
      i18n.changeLanguage(targetLng);
    }

    return () => {
      if (previousLanguageRef.current != null) {
        i18n.changeLanguage(previousLanguageRef.current);
        previousLanguageRef.current = null;
      }
    };
  }, [defaultLanguage]);
}
